import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React, { useMemo, useState } from 'react';

import { Box } from '@components/Restyle';
import { TaskChecklist } from '@graphql/generated';

import { TaskChecklistItem } from './TaskChecklistItem';

type Props = {
  width?: number;
  listTaskChecklist?: TaskChecklist[];
  showChecklistCompletedItems: boolean;
  canEditTaskSecondLevel: boolean;
  onCompletedTaskChecklistItem: (
    item: TaskChecklist,
    callback?: () => void
  ) => void;
  onSaveTaskChecklistItem: (name: string, item?: TaskChecklist) => void;
  onDeleteTaskChecklistItem: (itemId: string) => void;

  onReorder: (nextList: TaskChecklist[]) => void;
};

export const DndKitSortableChecklistListWeb: React.FC<Props> = ({
  width = 500,
  listTaskChecklist,
  showChecklistCompletedItems,
  canEditTaskSecondLevel,
  onCompletedTaskChecklistItem,
  onSaveTaskChecklistItem,
  onDeleteTaskChecklistItem,
  onReorder,
}) => {
  const data = useMemo(() => {
    const arr = listTaskChecklist || [];
    const completed = arr
      .filter((t) => t.completedAt !== null)
      .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
    const active = arr
      .filter((t) => t.completedAt === null)
      .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));

    return showChecklistCompletedItems ? [...completed, ...active] : active;
  }, [listTaskChecklist, showChecklistCompletedItems]);

  const [activeId, setActiveId] = useState<string | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | undefined>();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6,
      },
    })
  );

  const activeItem = useMemo(
    () => data.find((x) => x.id === activeId) || null,
    [activeId, data]
  );

  const handleDragEnd = (event: DragEndEvent) => {
    setSelectedItemId?.(undefined);

    const { active, over } = event;
    setActiveId(null);

    if (!over) return;
    if (active.id === over.id) return;

    const oldIndex = data.findIndex((x) => x.id === active.id);
    const newIndex = data.findIndex((x) => x.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const next = arrayMove(data, oldIndex, newIndex);

    onReorder(next);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={(e) => setActiveId(String(e.active.id))}
      onDragEnd={handleDragEnd}>
      <SortableContext
        items={data.map((x) => x.id)}
        strategy={verticalListSortingStrategy}>
        <div style={{ width }}>
          {data.map((item) => (
            <SortableRow
              key={item.id}
              id={item.id}
              width={width}
              item={item}
              onCompletedTaskChecklistItem={onCompletedTaskChecklistItem}
              onSaveTaskChecklistItem={onSaveTaskChecklistItem}
              onDeleteTaskChecklistItem={onDeleteTaskChecklistItem}
              selectedItemId={selectedItemId}
              setSelectedItemId={setSelectedItemId}
              canEditTaskSecondLevel={canEditTaskSecondLevel}
            />
          ))}
        </div>
      </SortableContext>

      {activeItem ? (
        <div style={{ opacity: 0, height: 0 }}>
          <TaskChecklistItem
            item={activeItem}
            width={width}
            canEditTaskSecondLevel={canEditTaskSecondLevel}
            onCompletedTaskChecklistItem={onCompletedTaskChecklistItem}
            onDeleteTaskChecklistItem={onDeleteTaskChecklistItem}
            onSaveTaskChecklistItem={onSaveTaskChecklistItem}
          />
        </div>
      ) : null}
    </DndContext>
  );
};

const SortableRow = ({
  id,
  item,
  width,
  canEditTaskSecondLevel,
  onCompletedTaskChecklistItem,
  onSaveTaskChecklistItem,
  onDeleteTaskChecklistItem,
  selectedItemId,
  setSelectedItemId,
}: {
  id: string;
  item: TaskChecklist;
  width: number;
  canEditTaskSecondLevel: boolean;
  onCompletedTaskChecklistItem: (item: TaskChecklist) => void;
  onSaveTaskChecklistItem: (name: string, item?: TaskChecklist) => void;
  onDeleteTaskChecklistItem: (itemId: string) => void;
  selectedItemId?: string | undefined;
  setSelectedItemId?: React.Dispatch<React.SetStateAction<string | undefined>>;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: 'default',
    opacity: isDragging ? 0.99 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Box flexDirection='row' alignItems='center' width={width}>
        <TaskChecklistItem
          item={item}
          width={width}
          canEditTaskSecondLevel={canEditTaskSecondLevel}
          onCompletedTaskChecklistItem={onCompletedTaskChecklistItem}
          onDeleteTaskChecklistItem={onDeleteTaskChecklistItem}
          onSaveTaskChecklistItem={onSaveTaskChecklistItem}
          attributes={
            item.completedAt || !canEditTaskSecondLevel ? undefined : attributes
          }
          listeners={
            item.completedAt || !canEditTaskSecondLevel ? undefined : listeners
          }
          selectedItemId={selectedItemId}
          setSelectedItemId={setSelectedItemId}
        />
      </Box>
    </div>
  );
};
